import React, { useMemo, useState } from 'react'
import { Card, Table, Button, Space } from 'antd'
import { PrinterOutlined, UserOutlined, CarOutlined, TagOutlined, BarChartOutlined } from '@ant-design/icons'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend
} from 'chart.js'
import ImpressoraDashboardModal from './ImpressoraDashboardModal'

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend)

const PrinterUsageGrid = ({ data = [], loading = false }) => {
  const [dashboardVisible, setDashboardVisible] = useState(false)
  const [selectedDashboardPrinterName, setSelectedDashboardPrinterName] = useState('')

  const handleDashboardClick = (printerName) => {
    setSelectedDashboardPrinterName(printerName)
    setDashboardVisible(true)
  }

  const handleDashboardClose = () => {
    setDashboardVisible(false)
    setSelectedDashboardPrinterName('')
  }

  const groupData = useMemo(() => {
    const arr = Array.isArray(data) ? data : []
    const printers = {}

    const initAgg = () => ({
      notasSet: new Set(),
      etiquetas: 0,
      etiquetasImp: 0,
      volumes: 0,
      peso: 0,
      m3: 0,
      clientesSet: new Set(),
      veiculosSet: new Set(),
      itemsRaw: []
    })

    const addItem = (node, item) => {
      const notaKey = `${item.Nota}`
      const etiquetaImpressa = item.STATUS_ETIQUETA === 1 ? 1 : 0
      const volumes = Number(item.VOLUMES ?? 0)
      const peso = parseFloat(item.PESO) || 0
      const m3 = parseFloat(item.M3 ?? 0) || 0

      node.dados.notasSet.add(notaKey)
      node.dados.etiquetas += 1
      node.dados.etiquetasImp += etiquetaImpressa
      node.dados.volumes += volumes
      node.dados.peso += peso
      node.dados.m3 += m3
      if (item.CLIENTE) node.dados.clientesSet.add(item.CLIENTE)
      if (item.CARRO) node.dados.veiculosSet.add(item.CARRO)
      node.dados.itemsRaw.push(item)
    }

    arr.forEach(item => {
      const printerKey = item.impressora || item.IMPRESSORA || 'Sem Impressora'
      if (!printers[printerKey]) {
        printers[printerKey] = {
          key: `imp:${printerKey}`,
          title: printerKey,
          icon: <PrinterOutlined />,
          nivel: 1,
          tipo: 'impressora',
          children: {},
          dados: initAgg()
        }
      }
      const printerNode = printers[printerKey]
      addItem(printerNode, item)

      // Veículo como segundo nível
      const veiculoKey = item.CARRO || 'Sem Veículo'
      if (!printerNode.children[veiculoKey]) {
        printerNode.children[veiculoKey] = {
          key: `${printerNode.key}:car:${veiculoKey}`,
          title: veiculoKey,
          icon: <CarOutlined />,
          nivel: 2,
          tipo: 'carro',
          children: {},
          dados: initAgg()
        }
      }
      const veiculoNode = printerNode.children[veiculoKey]
      addItem(veiculoNode, item)

      // Cliente como terceiro nível
      const clienteKey = item.CLIENTE || 'Sem Cliente'
      if (!veiculoNode.children[clienteKey]) {
        veiculoNode.children[clienteKey] = {
          key: `${veiculoNode.key}:cli:${clienteKey}`,
          title: clienteKey,
          icon: <UserOutlined />,
          nivel: 3,
          tipo: 'cliente',
          children: {},
          dados: initAgg()
        }
      }
      addItem(veiculoNode.children[clienteKey], item)
    })

    const finalize = (node) => {
      node.dados.notas = node.dados.notasSet.size
      node.dados.clientes = node.dados.clientesSet.size
      node.dados.veiculos = node.dados.veiculosSet.size
      delete node.dados.notasSet
      delete node.dados.clientesSet
      delete node.dados.veiculosSet
    }

    const toArray = (obj) => Object.values(obj).map(n => {
      n.children = Object.values(n.children).map(c => {
        c.children = Object.values(c.children)
        c.children.forEach(finalize)
        finalize(c)
        return c
      })
      finalize(n)
      return n
    })

    return toArray(printers)
  }, [data])

  const columns = [
    {
      title: 'Impressora',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{record.icon}</span>
          <span>{text}</span>
        </div>
      )
    },
    { title: 'Veículo', dataIndex: 'veiculo', key: 'veiculo' },
    { title: 'Etiquetas Imp', dataIndex: 'etiquetasImp', key: 'etiquetasImp', sorter: (a, b) => a.etiquetasImp - b.etiquetasImp },
    { title: 'Notas', dataIndex: 'notas', key: 'notas', sorter: (a, b) => a.notas - b.notas },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => {
        if (record.tipo === 'impressora') {
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<BarChartOutlined />}
                onClick={() => handleDashboardClick(record.title)}
              >
              </Button>
            </Space>
          )
        }
        return null;
      }
    }
  ]

  const mapNode = (node) => ({
    key: node.key,
    title: node.title,
    tipo: node.tipo,
    etiquetasImp: node.dados.etiquetasImp,
    notas: node.dados.notas,
    volumes: node.dados.volumes,
    peso: node.dados.peso,
    m3: node.dados.m3,
    cliente: node.tipo === 'cliente' ? node.title : '-',
    veiculo: node.tipo === 'carro' ? node.title : '-',
    icon: node.icon,
    children: Array.isArray(node.children) ? node.children.map(mapNode) : []
  })

  const tableData = useMemo(() => groupData.map(mapNode), [groupData])

  const chartData = useMemo(() => {
    const labels = groupData.map(p => p.title)
    const values = groupData.map(p => p.dados.etiquetasImp)
    return {
      labels,
      datasets: [
        {
          label: 'Etiquetas Impressas',
          data: values,
          backgroundColor: 'rgba(82, 196, 26, 0.6)'
        }
      ]
    }
  }, [groupData])

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Uso de Impressoras (Etiquetas Impressas)' }
    }
  }

  const selectedPrinterItems = useMemo(() => {
    if (!selectedDashboardPrinterName) return []
    const found = groupData.find(p => p.title === selectedDashboardPrinterName)
    return found?.dados?.itemsRaw || []
  }, [groupData, selectedDashboardPrinterName])

  return (
    <>
      <Card
        title="Impressoras - Uso por Veículo e Cliente"
        variant="borderless"
        style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginTop: 12 }}
      >
        <Table
          dataSource={tableData}
          columns={columns}
          loading={loading}
          size="small"
          style={{ fontSize: '12px' }}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          expandable={{
            rowExpandable: (record) => Array.isArray(record.children) && record.children.length > 0,
          }}
        />

        <div style={{ marginTop: 16 }}>
          <Bar data={chartData} options={chartOptions} />
        </div>
      </Card>

      <ImpressoraDashboardModal
        visible={dashboardVisible}
        onCancel={handleDashboardClose}
        printerName={selectedDashboardPrinterName}
        printerItems={selectedPrinterItems}
      />
    </>
  )
}

export default PrinterUsageGrid